import type { ErrandActionResponse } from '@backend/schemas/src/errand-actions'
import type { ErrandResponse } from '@backend/schemas/src/errands'
import { format } from 'date-fns'
import {
  AlertCircleIcon,
  ArrowRightLeft,
  FileCheck2,
  FileTextIcon,
  Flag,
  LoaderIcon,
  NotebookPenIcon,
} from 'lucide-react'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

import type { DocumentUploadStatus } from '@/api/hooks/useDocumentUploadStatus'
import { DRAFT_META, draftBaseType } from '@/components/organisms/errand-actions/errand-action-card'
import { Button } from '@/components/ui/button'
import { cn, DateUtils } from '@/lib/utils'
import {
  buildErrandTimeline,
  type ErrandTimelineEvent,
  type ReactionMessage,
} from '@/utils/errand-timeline.utils'

type Props = {
  actions: ErrandActionResponse[]
  errand: ErrandResponse
  messages: ReactionMessage[]
  onOpenNote(noteId: string): void
  onPreviewDocument(documentId: string): void
  onViewInChat(messageId: string): void
  /** Live per-document pipeline status from RTDB, keyed by document id. */
  uploadStatuses: Record<string, DocumentUploadStatus>
}

type UploadProgress = { isError: boolean; isLoading: boolean }

/**
 * An upload is still working until every document in it says `done`. The live channel is checked
 * for `error` too: it reports a failure before the refetch it triggers lands, so a row relying
 * only on the fetched document would keep spinning after the pipeline gave up.
 *
 * Same rule as the documents table — both views should agree on what "still working" means.
 */
const uploadProgress = (
  documents: ErrandResponse['documents'],
  uploadStatuses: Record<string, DocumentUploadStatus>,
): UploadProgress => {
  const isError = documents.some(
    document => document.status === 'error' || uploadStatuses[document.id] === 'error',
  )
  const isLoading = documents.some(
    document =>
      document.status !== 'done' &&
      document.status !== 'error' &&
      uploadStatuses[document.id] !== 'error',
  )

  return { isError, isLoading }
}

const eventIcon = (event: ErrandTimelineEvent, progress?: UploadProgress) => {
  if (event.kind === 'created') return <Flag size={18} />
  if (event.kind === 'status-change') return <ArrowRightLeft size={18} />
  if (event.kind === 'note') return <NotebookPenIcon size={18} />

  if (event.kind === 'upload') {
    if (progress?.isLoading) return <LoaderIcon className="animate-spin text-info" size={18} />
    if (progress?.isError) return <AlertCircleIcon className="text-error" size={18} />

    return <FileCheck2 size={18} />
  }

  return DRAFT_META[draftBaseType(event.action.type)].icon
}

/**
 * Chronological (newest-first) log of what happened in an errand — created, status changes,
 * uploads, and AI drafts — with the chat as the "depth": a row jumps to the chat message that
 * belongs to it. Uploads are one row per upload rather than per document, with a pill per file.
 */
export const ErrandTimeline: FC<Props> = ({
  actions,
  errand,
  messages,
  onOpenNote,
  onPreviewDocument,
}) => {
  const events = buildErrandTimeline(errand, actions, messages)

  return (
    <ol className="flex flex-col gap-6">
      {events.map(event => {
        const progress =
          event.kind === 'upload' ? uploadProgress(event.documents, uploadStatuses) : undefined

        // Drafts always anchor to the message that produced them; status changes and uploads only
        // once a reaction about them exists.
        const messageId =
          event.kind === 'draft'
            ? (event.action.sourceMessageId ?? undefined)
            : event.kind === 'status-change' || event.kind === 'upload' || event.kind === 'note'
              ? event.messageId
              : undefined

        return (
          <li
            className={cn('flex items-start gap-2 rounded-md -m-2 p-2 transition-colors', {
              'hover:bg-layer-2': messageId,
            })}
            key={event.id}
          >
            <div className="shrink-0 pt-0.5 text-text-secondary">{eventIcon(event, progress)}</div>
            <div className="flex w-full flex-col gap-0.5">
              {/* The label is the click target, not the whole row: an upload's pills are their
                  own buttons, and nesting a button inside a button is invalid. The hover itself
                  lives on the whole `<li>` so it wraps the icon and document pills too. */}
              {messageId ? (
                <button
                  className="flex flex-col gap-0.5 text-left"
                  onClick={() => onViewInChat(messageId)}
                  type="button"
                >
                  <EventHeader event={event} progress={progress} />
                </button>
              ) : (
                <EventHeader event={event} progress={progress} />
              )}
              {event.kind === 'upload' && (
                <DocumentPills documents={event.documents} onPreview={onPreviewDocument} />
              )}
              {event.kind === 'note' && <NotePill note={event.note} onOpen={onOpenNote} />}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

/**
 * One pill per uploaded file. Clicking opens the document preview — which is why the pills sit
 * outside the row's own click target rather than inside it.
 */
const DocumentPills: FC<{
  documents: ErrandResponse['documents']
  onPreview(documentId: string): void
}> = ({ documents, onPreview }) => (
  <div className="mt-1.5 flex flex-wrap gap-1.5">
    {documents.map(document => (
      <Button
        className="gap-1.5"
        key={document.id}
        onClick={() => onPreview(document.id)}
        size="sm"
        title={document.name}
        type="button"
        variant="secondary"
      >
        <FileTextIcon className="shrink-0" size={14} />
        <span className="max-w-[24ch] truncate">{document.name}</span>
      </Button>
    ))}
  </div>
)

/**
 * The note itself, as a pill that opens it. Sits outside the row's click target for the same
 * reason a document pill does: the label already goes to the chat, and this goes somewhere else.
 */
const NotePill: FC<{
  note: ErrandResponse['notes'][number]
  onOpen(noteId: string): void
}> = ({ note, onOpen }) => {
  const { t } = useTranslation()
  // A note opened before its background analysis has produced a title still needs a legible pill.
  const label = note.title || t('notesTable.untitled')

  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      <Button
        className="gap-1.5"
        onClick={() => onOpen(note.id)}
        size="sm"
        title={label}
        type="button"
        variant="secondary"
      >
        <NotebookPenIcon className="shrink-0" size={14} />
        <span className="max-w-[24ch] truncate">{label}</span>
      </Button>
    </div>
  )
}

const EventHeader: FC<{
  event: ErrandTimelineEvent
  progress?: UploadProgress
}> = ({ event, progress }) => {
  const { t } = useTranslation()

  return (
    <>
      <EventLabel event={event} progress={progress} />
      <time className="text-xs font-medium text-text-secondary">
        {DateUtils.formatRelativeDate(new Date(event.at), t)}
      </time>
    </>
  )
}

const EventLabel: FC<{
  event: ErrandTimelineEvent
  progress?: UploadProgress
}> = ({ event, progress }) => {
  const { t } = useTranslation()

  if (event.kind === 'created') {
    return <p className="font-medium">{t('errandsPage.timeline.createdEvent')}</p>
  }

  if (event.kind === 'status-change') {
    return (
      <p className="font-medium">
        {t('errandsPage.timeline.statusChanged', {
          from: t(`errandStatus.${event.from}`),
          to: t(`errandStatus.${event.to}`),
        })}
      </p>
    )
  }

  if (event.kind === 'upload') {
    return (
      <p className="font-medium">
        {progress?.isLoading
          ? t('errandsPage.timeline.documentUploadingEvent', {
              count: event.documents.length,
            })
          : t('errandsPage.timeline.documentEvent', {
              count: event.documents.length,
            })}
      </p>
    )
  }

  // No subtitle here: the note's title is what its pill below already shows, and repeating it
  // reads as two different things having happened.
  if (event.kind === 'note') {
    return <p className="font-medium">{t('errandsPage.timeline.noteEvent')}</p>
  }

  return (
    <>
      <p className="font-medium">{t('errandsPage.timeline.draftEvent')}</p>
      <p className="text-sm text-text-secondary">{event.action.title}</p>
    </>
  )
}

export default ErrandTimeline
